// src/services/settingsService.js
import { authenticatedFetch, handleApiError } from './api';
import logger from '../utils/debugLogger';

/**
* Fetches the application settings from the backend.
* This is designed to work with your adminConfigController.js.
* @returns {Promise<object>} A promise that resolves to a valid settings object.
*/
export const fetchSettings = async () => {
  // This endpoint matches the GET route in your adminConfigRoutes.js
  const endpoint = '/admin-config'; 
  
  // Define a default structure to ensure the app never crashes
  const defaultSettings = {
    alertSettings: { color: '#ff0000' },
    sensorSettings: { defaultColor: '#0000ff' },
    issueSettings: { defaultColor: '#ffff00' },
    modelMappings: {},
  };

  try {
    const settings = await authenticatedFetch(endpoint);

    // The backend returns the config in a 'config_value' property
    const configValue = settings.config_value;

    if (configValue && typeof configValue === 'object') {
      logger.log('SettingsService', 'Successfully fetched settings from backend.', configValue);
      // Merge fetched settings with defaults to ensure all keys are present
      return { ...defaultSettings, ...configValue };
    }
    
    logger.warn('SettingsService', 'Settings from backend were missing or in an invalid format. Using defaults.', settings);
    return defaultSettings;

  } catch (error) {
    logger.error("[SettingsService] CRITICAL: Could not fetch settings due to an API error. Using defaults.", error);
    return defaultSettings;
  }
};

/**
* Updates the application settings on the backend.
* @param {object} settingsData - The settings object to save.
* @returns {Promise<object>} The updated settings object from the server.
*/
export const updateSettings = async (settingsData) => {
  // This endpoint matches the POST route in your adminConfigRoutes.js
  const endpoint = '/admin-config';
  const options = {
    method: 'POST',
    // Your backend expects the data in a specific format
    body: JSON.stringify({
      key: 'main_settings',
      value: settingsData
    }),
  };
  logger.log('SettingsService', 'Updating settings...', settingsData);
  return await authenticatedFetch(endpoint, options);
};

/**
* Handles the upload of a new 3D model file.
* NOTE: Your backend does not currently have a route for this. This is a placeholder.
* @param {string} assetType - The type of asset this model is for (e.g., "Pump").
* @param {FormData} formData - The FormData object containing the file.
* @returns {Promise<object>}
*/
export const uploadModelForAssetType = async (assetType, formData) => {
  const endpoint = `/assets/models/upload?assetType=${encodeURIComponent(assetType)}`;
  const options = {
    method: 'POST',
    body: formData,
  };
  logger.log('SettingsService', `Uploading model for asset type: ${assetType}`);
  // Passing 'multipart/form-data' tells the fetch helper to not set a JSON content-type
  return await authenticatedFetch(endpoint, options, 'multipart/form-data');
};

/**
* Fetches Digital Twin settings from the backend.
* @returns {Promise<object>} The settings object.
*/
export const getDigitalTwinSettings = async () => {
 try {
  return await authenticatedFetch('/admin-config/settings/digital-twin');
 } catch (error) {
  console.error("Failed to fetch digital twin settings:", error);
  // Return default settings on failure to prevent app crash
  return {
   statusColors: {
    operational: '#00DD00',
    maintenance_required: '#FFA500',
    damaged: '#FF0000',
    decommissioned: '#808080',
    unknown: '#AAAAAA',
   }
  };
 }
};

/**
* Saves Digital Twin settings to the backend.
* @param {object} settings - The settings object to save.
* @returns {Promise<object>} The server response.
*/
export const saveDigitalTwinSettings = async (settings) => {
 return await authenticatedFetch('/admin-config/settings/digital-twin', {
  method: 'PUT',
  body: JSON.stringify(settings),
 });
};

// --- UPDATE: Add new generic functions for any config key ---
/**
 * Fetches a configuration object by its key.
 * @param {string} key The configuration key to fetch.
 * @returns {Promise<object>} The configuration object.
 */
export const getConfig = (key) => {
    const endpoint = `/admin/config/${key}`;
    return authenticatedFetch(endpoint).catch(handleApiError);
};

/**
 * Updates a configuration object by its key.
 * @param {string} key The configuration key to update.
 * @param {object} value The new configuration value object.
 * @returns {Promise<object>} The server's response.
 */
export const updateConfig = (key, value) => {
    const endpoint = `/admin/config/${key}`;
    const options = {
        method: 'PUT',
        body: JSON.stringify({ config_value: value }),
    };
    return authenticatedFetch(endpoint, options).catch(handleApiError);
};

/**
 * Fetches the user's saved dashboard layout from the backend.
 * Uses the authenticatedFetch helper for consistency.
 */
export const getDashboardLayout = async () => {
    const endpoint = '/admin/dashboard-layout';
    try {
        // Using the same authenticatedFetch as the rest of the file
        const layout = await authenticatedFetch(endpoint);
        return layout; // The backend directly returns the layout array
    } catch (error) {
        if (error.message.includes('404')) {
            console.log("No saved dashboard layout found for user.");
            return null;
        }
        console.error("Error fetching dashboard layout:", error);
        throw error;
    }
};

/**
 * Saves the user's dashboard layout to the backend.
 * @param {Array} layout - The array of dashboard widget configurations.
 */
export const saveDashboardLayout = (layout) => {
    const endpoint = '/admin/dashboard-layout';
    const options = {
        method: 'PUT',
        body: JSON.stringify(layout),
    };
    // Using the same authenticatedFetch as the rest of the file
    return authenticatedFetch(endpoint, options).catch(handleApiError);
};